// src/components/DateConflictModal.js
import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "../styles/DateConflictModal.css";
import { FaCalendarAlt, FaClock } from "react-icons/fa";


function DateConflictModal({ visible, minDate, maxDate, minHour, maxHour, onConfirm, onCancel, existingDateHours }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState("");
  const [error, setError] = useState("");
  const [isValid, setIsValid] = useState(false);

  const dateInputRef = useRef(null);
  const hourInputRef = useRef(null);
  const [minuteLimitError, setMinuteLimitError] = useState(false);


  useEffect(() => {
    if (!selectedDate || !selectedHour) {
      setIsValid(false);
      return;
    }

    const isoDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const fullKey = `${isoDate}T${selectedHour}`;

    if (existingDateHours?.includes(fullKey)) {
      setError("❌ A location with the same date and hour already exists.");
      setIsValid(false);
      return;
    }
  
    const selectedDay = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const minDay = minDate?.toISOString().split("T")[0];
    const maxDay = maxDate?.toISOString().split("T")[0];

    const selectedTime = selectedHour;
    const minTime = minHour || "00:00";
    const maxTime = maxHour || "23:59";
  
    let isValidTime = true;
  
    if (minDay === maxDay && selectedDay === minDay) {
      // ✅ All on same day → compare hours as strings
      if (selectedTime < minTime || selectedTime > maxTime) {
        isValidTime = false;
      }
    } else {
      // 🧠 Dates may differ → fallback to full datetime comparison
      const selectedFull = new Date(`${selectedDay}T${selectedTime}`);
      const minFull = minDate ? new Date(`${minDay}T${minTime}`) : null;
      const maxFull = maxDate ? new Date(`${maxDay}T${maxTime}`) : null;
  
      if ((minFull && selectedFull < minFull) || (maxFull && selectedFull > maxFull)) {
        isValidTime = false;
      }
    }
  
    if (!isValidTime) {
      setError(`❌ Time must be between ${minTime} and ${maxTime}`);
      setIsValid(false);
    } else {
      setError("");
      setIsValid(true);
    }
  }, [selectedDate, selectedHour, minDate, maxDate, minHour, maxHour]);

  const sameDay = minDate && maxDate && minDate.toDateString() === maxDate.toDateString();
  const dateStr = minDate?.toLocaleDateString("en-GB");

  let guidanceMessage = "";
  if (sameDay && minHour && maxHour) {
    guidanceMessage = `Choose a time between ${minHour} and ${maxHour} on ${dateStr}`;
  } else if (sameDay && minHour) {
    guidanceMessage = `Choose a time after ${minHour} on ${dateStr}`;
  } else if (sameDay && maxHour) {
    guidanceMessage = `Choose a time before ${maxHour} on ${dateStr}`;
  } else if (!sameDay && minDate && maxDate && minHour && maxHour) {
    guidanceMessage = `Choose a date and time between ${minDate.toLocaleDateString("en-GB")} at ${minHour} and ${maxDate.toLocaleDateString("en-GB")} at ${maxHour}`;
  } else if (!sameDay && maxDate && maxHour) {
    guidanceMessage = `Choose a date and time before ${maxDate.toLocaleDateString("en-GB")} at ${maxHour}`;
  } else if (!sameDay && minDate && minHour) {
    guidanceMessage = `Choose a date and time after ${minDate.toLocaleDateString("en-GB")} at ${minHour}`;
  }
  
  if (!visible) return null;

  const handleConfirm = () => {
    const isoDate = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    onConfirm(isoDate, selectedHour);
  };



  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h3>⚠️DATE AND HOUR CONFLICT⚠️</h3>
        <p style={{ textAlign: "center", marginBottom: "1rem" }}>
          {guidanceMessage}
        </p>
  
        <div className="modal-datetime-group">
          {/* Input de data com ícone de calendário */}
          <div className="modal-input-wrapper">
            <input
              type="date"
              className="modal-input modal-input--date"
              value={selectedDate ? selectedDate.toISOString().split("T")[0] : ""}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              onClick={() => {
                if (dateInputRef.current && typeof dateInputRef.current.showPicker === "function") {
                  dateInputRef.current.showPicker();
                }
              }}
              min={minDate?.toISOString().split("T")[0]}
              max={maxDate?.toISOString().split("T")[0]}
              ref={dateInputRef}
            />
            <FaCalendarAlt
              className="modal-input-icon clickable"
              onClick={(e) => {
                e.preventDefault();
                if (dateInputRef.current) {
                  dateInputRef.current.focus();
                  if (typeof dateInputRef.current.showPicker === "function") {
                    dateInputRef.current.showPicker();
                  }
                }
              }}
            />
          </div>
  
          {/* Input de hora com ícone de relógio */}
          <div className="modal-input-wrapper">
            <input
              type="text"
              placeholder="HH:MM"
              value={selectedHour}
              maxLength={5}
              onChange={(e) => {
                const value = e.target.value;
              
                const clean = value.replace(/[^0-9:]/g, "");
              
                let formatted = clean;
                if (/^\d{3,4}$/.test(clean)) {
                  formatted = clean.slice(0, 2) + ":" + clean.slice(2);
                }
              
                setSelectedHour(formatted);
              
                const completePattern = /^\d{2}:\d{2}$/;
                const strictTimeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
              
                if (completePattern.test(formatted) && !strictTimeRegex.test(formatted)) {
                  // Só mostra erro se estrutura estiver completa mas inválida
                  setMinuteLimitError(true);
                } else {
                  setMinuteLimitError(false);
                }
              }}              
              className="modal-input"
              ref={(el) => (hourInputRef.current = el)}
            />
            <FaClock
              className="modal-input-icon clickable"
              onClick={(e) => {
                e.preventDefault();
                if (hourInputRef.current?.showPicker) {
                  hourInputRef.current.showPicker();
                }
              }}
            />
          </div>
        </div>

        {minuteLimitError && (
          <div style={{ color: "#d9534f", marginTop: "8px", fontSize: "0.95rem" }}>
            ⚠️Minutes must be between 00 and 59.
          </div>
        )}
  
        {error && <div style={{ color: "red", marginTop: "8px" }}>{error}</div>}
  
        <div className="modal-buttons">
          <button onClick={onCancel}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedHour || !isValid || minuteLimitError}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );  
}

export default DateConflictModal;